import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { EASE, MaskLine, InviewMaskLine, FadeUp, RevealImage } from "../components/Reveal";
import Marquee from "../components/Marquee";
import Flag from "../components/Flag";
import { StockOpportunities, TurnkeySection, InspectionSection, ResultsSection } from "../components/HomeSections";
import BrandShowcase from "../components/BrandShowcase";

const HERO_IMG = "/images/hero.webp";
const SKYLINE = "/images/skyline.webp";
const INTERCHANGE = "/images/interchange.webp";

export default function Home() {
  const { t } = useLang();
  const heroRef = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", reduced ? "0%" : "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollToNext = () => {
    if (window.__lenis) window.__lenis.scrollTo(window.innerHeight * 0.92);
    else window.scrollTo({ top: window.innerHeight * 0.92, behavior: "smooth" });
  };

  return (
    <div className="bg-[#050607]" data-testid="home-page">
      {/* 01 — HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[620px] overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-[-12%]" style={{ y: imgY }}>
          <motion.img
            src={HERO_IMG}
            alt="VEYRONE — premium automotive sourcing, Dubai to international markets"
            fetchPriority="high"
            className="w-full h-full object-cover"
            initial={reduced ? false : { scale: 1.12 }}
            animate={reduced ? {} : { scale: 1 }}
            transition={{ duration: 2.4, ease: EASE }}
            style={{ filter: "saturate(0.65) contrast(1.08) brightness(0.9)" }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050607] via-[#050607]/35 to-[#050607]/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050607]/70 via-transparent to-transparent" />

        <motion.div style={{ opacity: fade }} className="relative z-10 h-full flex flex-col justify-end px-5 md:px-10 pb-24 md:pb-20">
          <div className="max-w-[1100px]">
            <motion.div
              className="eyebrow mb-6 md:mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              {t("hero.eyebrow")}
            </motion.div>
            <h1 className="font-display text-[34px] leading-[1.05] sm:text-[58px] lg:text-[64px] xl:text-[72px] text-[#F3F3F0] max-w-[1240px]">
              <MaskLine delay={0.35}>{t("hero.line1")}</MaskLine>
              <MaskLine delay={0.5}>{t("hero.line2")}</MaskLine>
              <MaskLine delay={0.65}><span className="text-[#999995]">{t("hero.line3")}</span></MaskLine>
            </h1>
            <motion.p
              className="mt-6 md:mt-8 max-w-xl text-[14px] md:text-[16px] leading-relaxed text-[#999995]"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            >
              {t("hero.sub")}
            </motion.p>
            <motion.div
              className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-3"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            >
              <Link to="/dealers" className="btn btn-solid justify-between" data-testid="hero-cta-dealers">
                <span>{t("hero.ctaDealers")}</span><span aria-hidden="true">→</span>
              </Link>
              <Link to="/private-clients" className="btn justify-between" data-testid="hero-cta-private">
                <span>{t("hero.ctaPrivate")}</span><span aria-hidden="true">→</span>
              </Link>
            </motion.div>
          </div>
        </motion.div>

        <div className="absolute bottom-0 left-0 right-0 z-10 hairline-t">
          <div className="px-5 md:px-10 py-4 flex items-center justify-between">
            <span className="mono text-[10px] tracking-[0.3em] text-[#666663]">{t("hero.micro")}</span>
            <button onClick={scrollToNext} data-testid="hero-scroll-btn" className="mono text-[10px] tracking-[0.3em] text-[#666663] hover:text-[#F3F3F0] transition-colors flex items-center gap-3">
              {t("hero.scroll")}
              <motion.span animate={reduced ? {} : { y: [0, 5, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>↓</motion.span>
            </button>
          </div>
        </div>
      </section>

      <Marquee />

      {/* 02 — WHY VEYRONE */}
      <section className="px-5 md:px-10 py-24 md:py-32" data-testid="why-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div className="self-start">
            <FadeUp><div className="eyebrow mb-8">01 — {t("why.eyebrow")}</div></FadeUp>
            <h2 className="font-display text-[34px] sm:text-[48px] lg:text-[56px] text-[#F3F3F0]">
              {t("why.title").split("\n").map((line, i) => (
                <InviewMaskLine key={i} delay={i * 0.12} className={i === 1 ? "text-[#999995]" : ""}>{line}</InviewMaskLine>
              ))}
            </h2>
            <RevealImage src={SKYLINE} alt="Dubai at night — VEYRONE origin market" className="mt-10 aspect-[4/3] w-full max-w-[560px]" />
            <div className="mono text-[10px] tracking-[0.24em] text-[#666663] mt-3 uppercase">{t("why.imgCaption")}</div>
          </div>
          <div>
            <FadeUp><p className="font-display text-[22px] md:text-[28px] leading-snug text-[#F3F3F0] mb-8">{t("why.p1")}</p></FadeUp>
            <FadeUp delay={0.1}><p className="text-[15px] md:text-[16px] leading-relaxed text-[#999995] max-w-lg">{t("why.p2")}</p></FadeUp>
            <div className="grid grid-cols-1 sm:grid-cols-2 border-t border-l border-white/10 mt-14">
              {t("why.pillars").map((p, i) => (
                <FadeUp key={p.t} delay={i * 0.07} className="border-b border-r border-white/10 p-6 md:p-8">
                  <div className="mono text-[11px] text-[#555654] mb-6">{String(i + 1).padStart(2, "0")}</div>
                  <h3 className="mono text-[12px] tracking-[0.22em] text-[#F3F3F0] mb-3">{p.t}</h3>
                  <p className="text-[13px] leading-relaxed text-[#8A8A86]">{p.d}</p>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 03 — DEMAND GUIDES THE SEARCH */}
      <section className="hairline-t bg-[#070809]" data-testid="demand-section">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative min-h-[320px] lg:min-h-[720px] overflow-hidden order-2 lg:order-1">
            <motion.img
              src={INTERCHANGE}
              alt="Dubai highway interchange at night"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              style={{ filter: "saturate(0.6) contrast(1.1) brightness(0.7)" }}
              initial={{ scale: 1.1 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 2, ease: EASE }}
            />
            <div className="absolute inset-0 bg-[#050607]/40" />
          </div>
          <div className="px-5 md:px-10 py-20 md:py-28 order-1 lg:order-2">
            <FadeUp><div className="eyebrow mb-8">02 — {t("demand.eyebrow")}</div></FadeUp>
            <h2 className="font-display text-[32px] sm:text-[44px] lg:text-[52px] text-[#F3F3F0] mb-8">
              {t("demand.title").split("\n").map((l, i) => <InviewMaskLine key={i} delay={i * 0.12}>{l}</InviewMaskLine>)}
            </h2>
            <FadeUp delay={0.1}><p className="text-[15px] leading-relaxed text-[#999995] max-w-lg mb-12">{t("demand.body")}</p></FadeUp>
            <div className="border-t border-white/10">
              {t("demand.steps").map((s, i) => (
                <FadeUp key={s} delay={i * 0.04}>
                  <div className="group flex items-center justify-between border-b border-white/10 py-4 hover:pl-3 transition-all duration-500" data-testid={`demand-step-${i + 1}`}>
                    <span className="mono text-[11px] tracking-[0.2em] text-[#F3F3F0]">{s}</span>
                    <span className="mono text-[11px] text-[#555654] group-hover:text-[#D1C7BD] transition-colors">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.2} className="mt-10">
              <Link to="/our-process" className="btn justify-between" data-testid="demand-cta">{t("demand.cta")}<span aria-hidden="true">→</span></Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* 04 — SELECTED OPPORTUNITIES (live stock) */}
      <StockOpportunities number="03" />

      {/* 05 — THE VEYRONE STANDARD */}
      <section className="hairline-t bg-[#070809] px-5 md:px-10 py-24 md:py-32" data-testid="standard-section">
        <div className="max-w-3xl mb-16">
          <FadeUp><div className="eyebrow mb-8">04 — {t("standard.eyebrow")}</div></FadeUp>
          <h2 className="font-display text-[34px] sm:text-[52px] text-[#F3F3F0]"><InviewMaskLine>{t("standard.title")}</InviewMaskLine></h2>
          <FadeUp delay={0.12}><p className="mt-5 text-[15px] text-[#999995]">{t("standard.sub")}</p></FadeUp>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 border-t border-l border-white/10">
          {t("standard.pillars").map((p, i) => (
            <FadeUp key={p.t} delay={i * 0.05} className="group border-b border-r border-white/10 p-7 md:p-9 hover:bg-white/[0.025] transition-colors duration-500">
              <div className="mono text-[11px] text-[#555654] mb-8">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="mono text-[12px] tracking-[0.24em] text-[#F3F3F0] mb-3">{p.t}</h3>
              <p className="text-[13px] leading-relaxed text-[#8A8A86]">{p.q}</p>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.1} className="mt-20 md:mt-28">
          <p className="font-display text-center text-[24px] sm:text-[36px] lg:text-[44px] leading-tight text-[#F3F3F0] max-w-5xl mx-auto">
            {t("standard.close").split("\n").map((l, i) => <InviewMaskLine key={i} delay={i * 0.15}>{l}</InviewMaskLine>)}
          </p>
        </FadeUp>
      </section>

      {/* CINEMATIC BRAND SHOWCASE (unnumbered band) */}
      <BrandShowcase />

      {/* 06 — TURNKEY SERVICE IN SPAIN */}
      <TurnkeySection number="05" />

      {/* 07 — INSPECTION YOU CAN SEE */}
      <InspectionSection number="06" />

      {/* 08 — MARKETS */}
      <section className="hairline-t px-5 md:px-10 py-24 md:py-32" data-testid="markets-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div>
            <FadeUp><div className="eyebrow mb-8">07 — {t("markets.eyebrow")}</div></FadeUp>
            <h2 className="font-display text-[34px] sm:text-[48px] text-[#F3F3F0]"><InviewMaskLine>{t("markets.title")}</InviewMaskLine></h2>
          </div>
          <FadeUp delay={0.15}>
            <Link to="/global-network" className="btn justify-between" data-testid="markets-cta">{t("markets.cta")}<span aria-hidden="true">→</span></Link>
          </FadeUp>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-white/10">
          <FadeUp className="border-b border-r border-white/10 p-6 md:p-8 bg-[#0A0B0C]">
            <div className="flex items-start justify-between mb-10"><Flag code="AE" /></div>
            <h3 className="mono text-[12px] tracking-[0.2em] text-[#F3F3F0] mb-2">{t("markets.origin")}</h3>
            <p className="mono text-[10px] tracking-[0.16em] text-[#666663] uppercase">{t("markets.originDesc")}</p>
          </FadeUp>
          {t("markets.items").map((m, i) => (
            <FadeUp key={m.name} delay={i * 0.04} className="border-b border-r border-white/10 p-6 md:p-8 hover:bg-white/[0.025] transition-colors duration-500" data-testid={`market-card-${m.name.toLowerCase()}`}>
              <div className="flex items-start justify-between mb-10"><Flag code={["ES", "DE", "FR", "BE", "NL", "IT", "PT"][i]} /></div>
              <h3 className="mono text-[12px] tracking-[0.2em] text-[#F3F3F0] mb-2">{m.name}</h3>
              <p className="mono text-[10px] tracking-[0.16em] text-[#666663] uppercase">{m.role}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* 09 — RESULTS */}
      <ResultsSection number="08" />

      {/* 10 — CTA BAND */}
      <section className="hairline-t bg-[#070809] px-5 md:px-10 py-28 md:py-36 text-center" data-testid="cta-band">
        <h2 className="font-display text-[36px] sm:text-[56px] lg:text-[64px] text-[#F3F3F0] mb-6">
          {t("ctaBand.title").split("\n").map((l, i) => <InviewMaskLine key={i} delay={i * 0.14}>{l}</InviewMaskLine>)}
        </h2>
        <FadeUp delay={0.2}>
          <p className="text-[14px] md:text-[16px] text-[#999995] max-w-xl mx-auto mb-12">{t("ctaBand.sub")}</p>
        </FadeUp>
        <FadeUp delay={0.3} className="flex flex-col sm:flex-row justify-center gap-3">
          <Link to="/dealers" className="btn btn-solid justify-between" data-testid="cta-band-dealers">{t("ctaBand.dealerBtn")}<span aria-hidden="true">→</span></Link>
          <Link to="/private-clients" className="btn justify-between" data-testid="cta-band-private">{t("ctaBand.privateBtn")}<span aria-hidden="true">→</span></Link>
        </FadeUp>
      </section>
    </div>
  );
}
